import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { postData, API_ENABLED } from "../../lib/api";
import { useSite } from "../../lib/SiteContext";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

export default function ContactHero() {
  const { t, i18n } = useTranslation();
  const { setting } = useSite();
  const [sending, setSending] = useState(false);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
    website: "", // honeypot: bots fill this, people never see it
  });

  const [errors, setErrors] = useState({
    name: false,
    email: false,
  });

  const validateForm = () => {
    let valid = true;
    let newErrors = { name: false, email: false };

    // Accept any script: the site serves English, Dari and Pashto visitors.
    if (formData.name.trim().length < 2 || /[0-9<>]/.test(formData.name)) {
      newErrors.name = true;
      valid = false;
    }

    // Validate email
    if (
      !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(formData.email) ||
      formData.email.trim() === ""
    ) {
      newErrors.email = true;
      valid = false;
    }

    setErrors(newErrors);
    return valid;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error(t("contactPage-invalid", "Please fill in all fields correctly."));
      return;
    }

    if (formData.message.trim().length < 5) {
      toast.error(t("contactPage-short", "Please write a short message."));
      return;
    }

    if (!API_ENABLED) {
      toast.error(
        t("contactPage-offline", "The contact form is unavailable. Please email or call us instead.")
      );
      return;
    }

    setSending(true);

    try {
      const response = await postData(`/contact?lang=${i18n.language || "en"}`, formData);

      toast.success(
        response.message || t("contactPage-success", "Thank you. Your message has been received.")
      );

      setFormData({ name: "", email: "", phone: "", subject: "", message: "", website: "" });
      setErrors({ name: false, email: false });
    } catch (error) {
      toast.error(
        error.payload?.message ||
          t("contactPage-failed", "Your message could not be sent. Please try again.")
      );
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen w-full">
      {/* Hero Section */}
      <div
        className="w-full min-h-96 mt-[64px] bg-cover bg-center relative"
        style={{
          backgroundImage: `url("https://cdn.pixabay.com/photo/2015/01/25/21/02/phone-612061_1280.jpg")`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="absolute inset-0 bg-black bg-opacity-50 flex flex-col justify-center items-center text-white text-center px-6">
          <h1 className="text-5xl font-bold mb-4">{t("contactPage-main-title")}</h1>
          <div className="bg-usetheme w-[30%] mt-2 h-1   mx-auto"></div>

          {/* <p className="text-xl mb-6">{t("contactPage-main-desc")}</p> */}
        </div>
      </div>

  

      {/* Contact Form */}
      <div className="w-full px-4 md:px-0 md:w-[1100px] mx-auto my-24">
        <h2 className="text-center text-4xl font-bold my-8 text-mstheme">
          {t("contactPage-msg-main-title")}
        </h2>
        <div className="w-full h-1 bg-gradient-to-r from-transparent via-mstheme to-transparent mb-12"></div>

        <form className="space-y-8" onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              placeholder={t("contactPage-input-name-placeholder")}
              className={`w-full p-4 border rounded-lg focus:outline-none ${
                errors.name ? "border-red-500" : "border-gray-300 focus:border-mstheme"
              }`}
            />
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              placeholder={t("contactPage-input-email-placeholder")}
              className={`w-full p-4 border rounded-lg focus:outline-none ${
                errors.email ? "border-red-500" : "border-gray-300 focus:border-mstheme"
              }`}
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <input
              type="text"
              name="phone"
              value={formData.phone}
              onChange={handleInputChange}
              placeholder={t("contactPage-input-phone-placeholder", "Your Phone Number")}
              className="w-full p-4 border border-gray-300 rounded-lg focus:outline-none focus:border-mstheme"
            />
            <input
              type="text"
              name="subject"
              value={formData.subject}
              onChange={handleInputChange}
              placeholder={t("contactPage-input-subject-placeholder", "Subject")}
              className="w-full p-4 border border-gray-300 rounded-lg focus:outline-none focus:border-mstheme"
            />
          </div>

          {/* Honeypot field, hidden from visitors */}
          <input
            type="text"
            name="website"
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
            value={formData.website}
            onChange={handleInputChange}
            className="hidden"
          />

          <textarea
            rows="5"
            name="message"
            maxLength={2000}
            value={formData.message}
            onChange={handleInputChange}
            placeholder={t("contactPage-input-msg-placeholder")}
            className="w-full p-4 border border-gray-300 rounded-lg focus:outline-none focus:border-mstheme"
          ></textarea>
          <span className="flex justify-end">{formData.message.length}/2000</span>
          <button
            type="submit"
            disabled={sending}
            className="w-full p-4 bg-mstheme text-white rounded-lg font-semibold hover:bg-opacity-90 transition duration-300 disabled:opacity-60"
          >
            {sending ? t("contactPage-sending", "Sending…") : t("contactPage-msg-btn")}
          </button>
        </form>
      </div>

      <ToastContainer />

          {/* Map Section */}
          <div className="w-full px-4 md:px-0 md:w-[1100px] mx-auto my-12">
        <h2 className="text-center text-4xl font-bold my-8 text-mstheme">
          {t("contactPage-map-title")}
        </h2>
        <div className="w-full h-[500px] rounded-lg overflow-hidden shadow-lg">
          <iframe
            title={setting("contact_address", "Kabul, Afghanistan")}
            src={
              setting(
                "google_map_embed",
                "https://www.google.com/maps?q=Taimani+Project,+Kabul,+Afghanistan&output=embed"
              )
            }
            width="100%"
            height="100%"
            allowFullScreen=""
            loading="lazy"
          ></iframe>
        </div>
      </div>
    </div>
  );
}
